import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { RotateCcw, Building2, Wallet, X } from 'lucide-react'
import {
    get_my_return_requests,
    cancel_return_request,
    messageClear
} from '../../store/reducers/returnReducer'

const STATUS_BADGE = {
    pending: 'bg-yellow-100 text-yellow-700',
    approved: 'bg-blue-100 text-blue-700',
    rejected: 'bg-red-100 text-red-700',
    completed: 'bg-green-100 text-green-700',
    cancelled: 'bg-gray-100 text-gray-500',
}

const STATUS_LABEL = {
    pending: 'Pending Review',
    approved: 'Approved — Refund Processing',
    rejected: 'Rejected',
    completed: 'Refund Sent',
    cancelled: 'Cancelled',
}

function RefundAccountDisplay({ info }) {
    if (!info?.type) return null
    if (info.type === 'bank') {
        return (
            <div className="flex items-start gap-1.5 text-xs text-gray-500 mt-1">
                <Building2 size={12} className="mt-0.5 flex-shrink-0" />
                <span>{info.bankName} · {info.accountNumber} · {info.accountName}</span>
            </div>
        )
    }
    return (
        <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-1">
            <Wallet size={12} className="flex-shrink-0" />
            <span>PayPal: {info.paypalEmail}</span>
        </div>
    )
}

export default function ReturnRequests() {
    const dispatch = useDispatch()
    const { returnRequests, totalReturns, successMessage, errorMessage } = useSelector(s => s.return)
    const [statusFilter, setStatusFilter] = useState('all')
    const [cancelling, setCancelling] = useState(null)

    useEffect(() => {
        dispatch(get_my_return_requests({ status: statusFilter }))
    }, [dispatch, statusFilter])

    useEffect(() => {
        if (successMessage) { toast.success(successMessage); dispatch(messageClear()) }
        if (errorMessage) { toast.error(errorMessage); dispatch(messageClear()) }
    }, [successMessage, errorMessage, dispatch])

    const handleCancel = async (id) => {
        setCancelling(id)
        await dispatch(cancel_return_request(id))
        dispatch(get_my_return_requests({ status: statusFilter }))
        setCancelling(null)
    }

    return (
        <div className="bg-white border border-gray-100 shadow-sm rounded-lg p-6">
            <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                    <RotateCcw size={18} className="text-indigo-600" />
                    <h2 className="text-xl font-semibold text-gray-900">My Returns</h2>
                </div>
                <select
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                    className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                    <option value="all">All</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                </select>
            </div>

            {returnRequests.length === 0 ? (
                <div className="text-center py-16">
                    <RotateCcw size={32} className="text-gray-200 mx-auto mb-3" />
                    <p className="text-gray-400 text-sm">No return requests found.</p>
                    <p className="text-gray-400 text-xs mt-1">
                        You can request a return from your{' '}
                        <Link to="/dashboard/my-orders" className="text-indigo-600 hover:underline">order details</Link>.
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {returnRequests.map(r => (
                        <div key={r._id} className="border border-gray-100 rounded-lg p-4">
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="flex gap-3">
                                    {r.product?.image && (
                                        <img src={r.product.image} alt="" className="w-14 h-14 object-cover rounded-md border border-gray-100 flex-shrink-0" />
                                    )}
                                    <div>
                                        <div className="font-medium text-gray-900 text-sm">{r.product?.name}</div>
                                        <div className="text-xs text-gray-500 mt-0.5 capitalize">
                                            {r.reason?.replace(/_/g, ' ')}
                                            {r.reasonDetail && ` — ${r.reasonDetail}`}
                                        </div>
                                        <RefundAccountDisplay info={r.refundAccountInfo} />
                                    </div>
                                </div>
                                <div className="text-right flex-shrink-0">
                                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_BADGE[r.status]}`}>
                                        {STATUS_LABEL[r.status] || r.status}
                                    </span>
                                    <div className="text-xs text-gray-400 mt-1">
                                        {new Date(r.createdAt).toLocaleDateString()}
                                    </div>
                                </div>
                            </div>

                            {/* Refund amount (if approved/completed) */}
                            {r.refundAmount != null && (
                                <div className="mt-3 text-sm">
                                    <span className="text-gray-500">Refund amount: </span>
                                    <span className="font-semibold text-green-700">${r.refundAmount.toFixed(2)}</span>
                                </div>
                            )}

                            {/* Admin notes */}
                            {r.adminNotes && (
                                <div className="mt-2 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                                    <span className="font-medium text-gray-700">Note: </span>{r.adminNotes}
                                </div>
                            )}

                            {/* Cancel button for pending requests */}
                            {r.status === 'pending' && (
                                <div className="mt-3 flex justify-end">
                                    <button
                                        onClick={() => handleCancel(r._id)}
                                        disabled={cancelling === r._id}
                                        className="flex items-center gap-1 text-xs text-red-600 hover:text-red-800 transition-colors disabled:opacity-60"
                                    >
                                        <X size={11} />
                                        {cancelling === r._id ? 'Cancelling...' : 'Cancel Request'}
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
